import { useContext, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faEnvelope, faKey } from '@fortawesome/free-solid-svg-icons';
import { AuthContext, AuthContextType, ToastContext, ToastContextType, LangContext, LangContextType } from '../utilities/customHooks';
import { useActionIndicator } from '../utilities/useActionIndicator';
import ActionCard from './ActionCard';
import EmailVerificationModal from './EmailVerificationModal';
import PasswordChangeModal from './PasswordChangeModal';
import Button from './Button';

interface ActionIndicatorProps {
  user: {
    isVerified: boolean | null;
    requiresPasswordReset: boolean | null;
    email: string | null;
  };
}

/**
 * Floating action indicator for soft enforcement of auth requirements
 * Shows badge with pulse animation when actions are pending
 * Expands to show action cards with CTAs
 * Includes email verification and password change modals
 */
function ActionIndicator({ user }: ActionIndicatorProps) {
  const { langState } = useContext(LangContext) as LangContextType;
  const { toastState } = useContext(ToastContext) as ToastContextType;
  const { refreshUserInfo } = useContext(AuthContext) as AuthContextType;
  const panelRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLButtonElement>(null);

  const {
    hasPendingActions,
    pendingActionCount,
    needsEmailVerification,
    needsPasswordReset,
    isExpanded,
    isDismissed,
    emailModalOpen,
    passwordModalOpen,
    togglePanel,
    dismissIndicator,
    openEmailModal,
    closeEmailModal,
    openPasswordModal,
    closePasswordModal,
  } = useActionIndicator({
    isVerified: user.isVerified,
    requiresPasswordReset: user.requiresPasswordReset,
  });

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isExpanded &&
        panelRef.current &&
        badgeRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !badgeRef.current.contains(event.target as Node)
      ) {
        togglePanel();
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, togglePanel]);

  // Email verification completes in a DIFFERENT tab (the emailed link), so the
  // isVerified flag in AuthContext goes stale and this indicator would sit on
  // screen until a full reload. While verification is pending, re-pull
  // user-info whenever the user returns to this tab.
  useEffect(() => {
    if (!needsEmailVerification) return;

    const handleFocus = () => {
      if (document.visibilityState !== 'visible') return; // ignore tab-hide events
      // Best-effort refresh: a failed fetch (offline refocus) just means the
      // indicator stays until the next focus — never an unhandled rejection.
      refreshUserInfo().catch(() => {});
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [needsEmailVerification, refreshUserInfo]);

  // Handle Escape key to close panel
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isExpanded) {
        togglePanel();
        badgeRef.current?.focus(); // Return focus to badge
      }
    };

    if (isExpanded) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded, togglePanel]);

  // Don't render if no pending actions or dismissed
  if (!hasPendingActions || isDismissed) {
    return null;
  }

  // Dynamic positioning based on Toast visibility
  const toastOffset = toastState.isShow ? '-translate-y-36' : '';

  const handlePasswordSuccess = async () => {
    // Refresh auth state to get updated requiresPasswordReset flag
    await refreshUserInfo();
  };

  return (
    <>
      {/* Floating Badge */}
      <button
        ref={badgeRef}
        onClick={togglePanel}
        className={`
          fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full
          bg-cYellow shadow-lg hover:brightness-110 transition-all duration-200
          flex items-center justify-center
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          ${toastOffset}
          ${isExpanded ? 'scale-95' : ''}
          ${!isExpanded ? 'animate-pulse-attention' : ''}
        `}
        aria-label={
          langState === 'fr'
            ? `Vous avez ${pendingActionCount} action${pendingActionCount > 1 ? 's' : ''} en attente. Cliquez pour voir les détails.`
            : `You have ${pendingActionCount} pending action${pendingActionCount > 1 ? 's' : ''}. Click to view details.`
        }
        aria-expanded={isExpanded}
        aria-haspopup="true"
        aria-controls="action-panel"
      >
        <FontAwesomeIcon icon={faBell} className="text-white" size="lg" />

        {/* Badge count (if multiple actions) */}
        {pendingActionCount > 1 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-cDenied rounded-full flex items-center justify-center text-white text-xs font-bold">
            {pendingActionCount}
          </span>
        )}
      </button>

      {/* Expanded Panel */}
      {isExpanded && (
        <div
          ref={panelRef}
          id="action-panel"
          role="dialog"
          aria-labelledby="panel-title"
          aria-modal="false"
          className={`
            fixed bottom-24 right-6 z-50 w-full max-w-sm
            bg-white rounded-t-xl rounded-bl-xl rounded-br-none
            shadow-2xl p-5 space-y-4
            animate-slide-up
            ${toastOffset}
          `}
          style={{ maxHeight: '480px', overflowY: 'auto' }}
        >
          {/* Header */}
          <h2 id="panel-title" className="text-lg font-semibold text-primaryDark border-b border-gray-200 pb-2">
            {langState === 'fr' ? 'Complétez votre compte' : 'Complete Your Account Setup'}
          </h2>

          {/* Action Cards */}
          <div className="space-y-3">
            {needsEmailVerification && (
              <ActionCard
                icon={faEnvelope}
                title={langState === 'fr' ? 'Vérifiez votre email' : 'Verify Your Email'}
                description={
                  langState === 'fr'
                    ? 'Confirmez votre adresse email pour assurer la sécurité de votre compte et recevoir des mises à jour importantes.'
                    : 'Confirm your email address to ensure account security and receive important updates.'
                }
                primaryCTA={{
                  text: langState === 'fr' ? 'Vérifier maintenant' : 'Verify Now',
                  onClick: openEmailModal,
                }}
              />
            )}

            {needsPasswordReset && (
              <ActionCard
                icon={faKey}
                title={langState === 'fr' ? 'Mettez à jour votre mot de passe' : 'Update Your Password'}
                description={
                  langState === 'fr'
                    ? 'Votre mot de passe ne répond pas à nos normes de sécurité actuelles (12+ caractères, majuscule, minuscule, chiffre, caractère spécial).'
                    : 'Your password doesn\'t meet our current security standards (12+ characters, uppercase, lowercase, number, special character).'
                }
                primaryCTA={{
                  text: langState === 'fr' ? 'Changer le mot de passe' : 'Change Password',
                  onClick: openPasswordModal,
                }}
              />
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Dismiss Button */}
          <div className="flex justify-end">
            <button
              onClick={dismissIndicator}
              className="text-sm text-gray-600 hover:text-primary transition underline"
            >
              {langState === 'fr' ? 'Me le rappeler plus tard' : 'Remind Me Later'}
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <EmailVerificationModal
        isOpen={emailModalOpen}
        onClose={closeEmailModal}
        userEmail={user.email || ''}
      />

      <PasswordChangeModal
        isOpen={passwordModalOpen}
        onClose={closePasswordModal}
        onSuccess={handlePasswordSuccess}
      />

      {/* Animations */}
      <style>{`
        @keyframes pulse-attention {
          0%, 100% {
            box-shadow: 0 4px 12px rgba(252, 176, 23, 0.25);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 4px 24px rgba(252, 176, 23, 0.5);
            transform: scale(1.05);
          }
        }

        .animate-pulse-attention {
          animation: pulse-attention 3s ease-in-out infinite;
        }

        @keyframes slide-up {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slide-up {
          animation: slide-up 200ms ease-out;
        }
      `}</style>
    </>
  );
}

export default ActionIndicator;
